import React, { useMemo, useState } from "react";
import { Box, Button, Flex, Heading, Icon } from "@telia/teddy";
import _partition from "lodash/partition";
import _values from "lodash/values";
import { useSelector } from "react-redux";
import { CART_ITEM_SUBSCRIPTION_TYPES, NEW_ACCOUNT_VALUE } from "../../../../../../constants";
import { RootState } from "../../../../../../models";
import { CustomerExistingAccount } from "../../../../../../models/state/retailer";
import {
    getCustomerExistingAccounts,
    getCustomerSelectedAccountId,
    getExistingPhoneNumberLookup,
    hasCustomerLookupOrCreditCheckBeenPerformed,
} from "../../../state/selectors/customerSelectors";
import { getAllItemsOfType } from "../../../../checkout/state/selectors/cartSelectors";
import { getSubscriptions } from "../../../../checkout/state/selectors/subscriptionsSelectors";
import {
    accountHasPhoneNumber,
    calculateEffectivePrice,
    getSubscriptionTitleWithBreakageFee,
    getYearlyCommitmentDiscountLabel,
    isSubscriptionActive,
} from "../../../utils";
import { CustomerSection } from "../CustomerSection";
import { RetailerSubscriptionCard } from "../RetailerSubscriptionCard";
import { getAccountFamilyDiscounts } from "../../organisms/ChooseSubscription/familyDiscount";
import "./CustomerDeals.less";

interface AccountTreeProps {
    account: CustomerExistingAccount;
    cartOfferingCodes: string[];
    cartSubscriptionByPhone: Record<string, ReturnType<typeof getSubscriptions>[string]>;
    isSelectedAccount: boolean;
    lookupPhoneNumber?: string;
    noAccordion?: boolean;
    isCustomerDataFetched: boolean;
}

const AccountTree: React.FC<AccountTreeProps> = ({
    account,
    cartOfferingCodes,
    cartSubscriptionByPhone,
    isSelectedAccount,
    lookupPhoneNumber,
    noAccordion,
    isCustomerDataFetched,
}) => {
    const [isOpen, setIsOpen] = useState(() => accountHasPhoneNumber(account, lookupPhoneNumber));

    const [activeSubscriptions, inactiveSubscriptions] = useMemo(() => {
        return _partition([...account.subscriptions], (sub) => isSubscriptionActive(sub));
    }, [account.subscriptions]);

    const effectiveCartSubscriptionByPhone = useMemo(
        () => (isSelectedAccount ? cartSubscriptionByPhone : {}),
        [cartSubscriptionByPhone, isSelectedAccount],
    );

    const phoneNumbersInCart = useMemo(
        () => new Set(Object.keys(effectiveCartSubscriptionByPhone)),
        [effectiveCartSubscriptionByPhone],
    );

    const cartPhoneNumbersToOfferingCode = useMemo(
        () =>
            Object.fromEntries(
                Object.entries(effectiveCartSubscriptionByPhone).map(([phone, sub]) => [phone, sub.code]),
            ),
        [effectiveCartSubscriptionByPhone],
    );

    const effectiveCartOfferingCodes = useMemo(
        () => (isSelectedAccount ? cartOfferingCodes : []),
        [cartOfferingCodes, isSelectedAccount],
    );

    const familyDiscounts = useMemo(
        () =>
            getAccountFamilyDiscounts(activeSubscriptions, effectiveCartOfferingCodes, cartPhoneNumbersToOfferingCode),
        [activeSubscriptions, effectiveCartOfferingCodes, cartPhoneNumbersToOfferingCode],
    );

    if (
        account.accountId === NEW_ACCOUNT_VALUE ||
        account.subscriptions.length === 0 ||
        account.accountId === undefined
    ) {
        return null;
    }

    const subscriptionsContent = (
        <Box className="customer-deals__subscriptions" ml={noAccordion ? undefined : "200"}>
            {activeSubscriptions.map((sub, i) => {
                const isChanged = phoneNumbersInCart.has(sub.phoneNumber);
                const changedSubscription = effectiveCartSubscriptionByPhone[sub.phoneNumber];
                const { effectivePrice, priceStriketrough } = isChanged
                    ? calculateEffectivePrice(
                          {
                              price: changedSubscription?.price?.monthly?.amountSubscription ?? 0,
                              priceLines: changedSubscription?.price?.monthlyLines?.amountSubscription,
                              offeringCode: changedSubscription?.code,
                          },
                          familyDiscounts[i],
                      )
                    : calculateEffectivePrice(sub, familyDiscounts[i]);
                const changedName = changedSubscription
                    ? `${sub.phoneNumber} - ${changedSubscription.name}`
                    : undefined;

                return (
                    <RetailerSubscriptionCard
                        id={sub.phoneNumber}
                        key={`${sub.phoneNumber}-${sub.offeringCode ?? "unknown"}-${sub.status ?? ""}`}
                        disableNavigation={sub.isSecretNumber || !isCustomerDataFetched}
                        isActive={isChanged ? undefined : isSubscriptionActive(sub)}
                        familyDiscount={familyDiscounts[i]}
                        yearlyCommitmentLabel={getYearlyCommitmentDiscountLabel(
                            isChanged ? (changedSubscription?.code ?? sub.offeringCode) : sub.offeringCode,
                        )}
                        changedFromName={isChanged ? sub.offeringName : undefined}
                        title={getSubscriptionTitleWithBreakageFee(sub, isCustomerDataFetched)}
                        name={
                            isChanged && changedName ? (
                                changedName
                            ) : sub.isSecretNumber ? (
                                <>
                                    {sub.maskedPhoneNumber} (hemmelig nummer)
                                    <br />
                                    <i>Bruk manuelt skjema</i>
                                </>
                            ) : sub.offeringName ? (
                                `${sub.phoneNumber} - ${sub.offeringName}`
                            ) : (
                                <>
                                    {sub.phoneNumber} - <i>Utgått abo</i>
                                </>
                            )
                        }
                        price={effectivePrice || <i>n/a</i>}
                        priceSuffix={effectivePrice ? ",-/md." : ""}
                        priceStriketrough={priceStriketrough}
                        isChanged={isChanged}
                        changeScope={changedSubscription?.existingSubscriptionUpdateScope}
                    />
                );
            })}
            {inactiveSubscriptions.length > 0 && (
                <p>
                    <i>
                        Det er {inactiveSubscriptions.length}{" "}
                        {inactiveSubscriptions.length === 1 ? "inaktivt abonnement" : "inaktive abonnement"} på denne
                        fakturaen
                    </i>
                </p>
            )}
        </Box>
    );

    if (noAccordion) {
        return <Box className="customer-deals__account">{subscriptionsContent}</Box>;
    }

    return (
        <Box className={`customer-deals__account${isOpen ? " customer-deals__account--open" : ""}`}>
            <Flex direction="row" align="center" gap="100">
                <Button
                    variant="text"
                    size="sm"
                    className="customer-deals__account-toggle"
                    onClick={() => setIsOpen(!isOpen)}
                    aria-expanded={isOpen}
                    aria-label={`${isOpen ? "Lukk" : "Åpne"} fakturanr. ${account.accountId}`}
                >
                    <Heading mb="100" as="h3" variant="title-100" className="customer-deals__account-heading">
                        <Icon name={isOpen ? "chevron-down" : "chevron-right"} aria-hidden="true" focusable="false" />{" "}
                        Fakturanr. {account.accountId} ({account.subscriptions.length} abo)
                    </Heading>
                </Button>
            </Flex>
            {isOpen && subscriptionsContent}
        </Box>
    );
};

const CustomerDeals = () => {
    const existingAccounts = useSelector((state: RootState) => getCustomerExistingAccounts(state));
    const selectedAccountId = useSelector((state: RootState) => getCustomerSelectedAccountId(state));
    const lookupPhoneNumber = useSelector((state: RootState) => getExistingPhoneNumberLookup(state));
    const cartSubscriptionItems = useSelector((state: RootState) =>
        getAllItemsOfType(state, ...CART_ITEM_SUBSCRIPTION_TYPES),
    );
    const cartSubscriptions = useSelector((state: RootState) => getSubscriptions(state));
    const isCustomerDataFetched = useSelector((state: RootState) => hasCustomerLookupOrCreditCheckBeenPerformed(state));

    const cartSubscriptionByPhone = useMemo(
        () =>
            Object.fromEntries(
                _values(cartSubscriptions)
                    .filter(
                        (sub): sub is typeof sub & { phoneNumber: NonNullable<typeof sub.phoneNumber> } =>
                            !!sub.phoneNumber?.selectedPhoneNumber,
                    )
                    .map((sub) => [sub.phoneNumber.selectedPhoneNumber, sub]),
            ),
        [cartSubscriptions],
    );

    const cartOfferingCodes = useMemo(
        () => cartSubscriptionItems.map((item) => item.id).filter((id): id is string => Boolean(id)),
        [cartSubscriptionItems],
    );
    const numberOfAccounts = existingAccounts.length;

    if (numberOfAccounts > 0) {
        return (
            <CustomerSection className="customer-deals" heading="Eksisterende abonnementer">
                {existingAccounts.map((account) => (
                    <AccountTree
                        key={account.accountId}
                        account={account}
                        cartOfferingCodes={cartOfferingCodes}
                        cartSubscriptionByPhone={cartSubscriptionByPhone}
                        isSelectedAccount={account.accountId === selectedAccountId}
                        lookupPhoneNumber={lookupPhoneNumber}
                        noAccordion={numberOfAccounts === 1}
                        isCustomerDataFetched={isCustomerDataFetched}
                    />
                ))}
            </CustomerSection>
        );
    }
    return null;
};

export default CustomerDeals;
